import { useEffect, useState } from 'react';
import {
  ActionFunction,
  LoaderFunction,
  Navigate,
  Outlet,
  useNavigation,
  useParams,
} from 'react-router-dom';
import { paths, TaskStatus, TaskType } from '@mosey/api-types';
import { formDataToObject } from '@mosey/utils/data/api';
import { LoadingTasks } from '@mosey/components/graphics/LoadingTasks.tsx';
import { ResolverSidebar } from '../resolver/Sidebar';
import {
  useTask,
  useTasks,
  useResolverUrl,
  useTaskActionData,
  useNextResolverUrl,
  useIsAutomationRoute,
  useIsStandaloneRoute,
  useTasksFrameworkLocation,
  useStandaloneTaskUrl,
} from '../utils/hooks';
import { api, apiBatch } from '../../../utils/fetchApi';
import { ResolverType, TaskRouteParams } from '../utils/types';
import { TaskQuestion } from './TaskQuestion';
import { TaskRequirement } from './TaskRequirement';
import { TaskSummary } from './TaskSummary';
import { Loading } from '../../Loading';
import { TaskActionResponse } from './types';
import { setNewTasksForSession } from '../utils/session';
import { TaskContainer } from './TaskContainer';
import { saveCallbackUrlFromLoader } from '../utils';

type AnswerResponse =
  paths['/api/compliance/tasks/{task_public_id}/answer']['post']['responses']['200']['content']['application/json'];
type BulkAnswerBody =
  paths['/api/compliance/bulk_tasks/{task_public_id}/answer']['post']['requestBody']['content']['application/json'];

export const action: ActionFunction = async ({
  params: { taskId, locationId, resolverType, wizardSlug },
  request,
}): Promise<TaskActionResponse | Response> => {
  const formData = await request.formData();
  const { intent, ...data } = Object.fromEntries(formData);
  let response;
  let newTasks: AnswerResponse = [];

  if (intent === 'assign-to') {
    try {
      response = await api({
        url: `/api/compliance/tasks/${taskId}`,
        method: request.method,
        body: {
          assigned_user_id: data.assigned_user_id || null,
        },
      });
    } catch (_error) {
      return {
        errors: {
          submit: 'Something went wrong, please try again.',
        },
        resolved: false,
        newTasks: [],
      };
    }

    return {
      errors: {},
      resolved: false,
      newTasks: [],
    };
  }

  // TODO: Better errors!!!

  if (intent === 'mark-as-done') {
    try {
      response = await api({
        url: `/api/compliance/tasks/${taskId}/status`,
        method: request.method,
        body: {
          status: TaskStatus.done,
          scoped_to_single_location: !!locationId,
        },
      });
    } catch (_error) {
      return {
        errors: {
          submit: 'Something went wrong, please try again.',
        },
        resolved: false,
        newTasks: [],
      };
    }

    newTasks = await response.json();
  } else if (intent === 'answer-question') {
    const answers = Object.values(data);

    if (answers.length === 0) {
      return {
        errors: {
          submit: 'Please answer the question to proceed.',
        },
        resolved: false,
        newTasks: [],
      };
    }

    const [answer] = answers;

    try {
      response = await api({
        url: `/api/compliance/tasks/${taskId}/answer`,
        method: request.method,
        body: {
          answer,
          scoped_region_code: locationId, // expected to be undefined if outside of a location scoped view
        },
      });
    } catch (_error) {
      return {
        errors: {
          submit: 'Something went wrong, please try again.',
        },
        resolved: false,
        newTasks: [],
      };
    }

    newTasks = await response.json();
  } else if (intent === 'bulk-answer') {
    const body: BulkAnswerBody = {
      answers: formDataToObject(formData, ['intent']),
    };

    try {
      response = await api({
        url: `/api/compliance/bulk_tasks/${taskId}/answer${wizardSlug ? `?wizard_public_id=${wizardSlug}` : ''}`,
        method: request.method,
        body,
      });
    } catch (error) {
      let submit = 'Something went wrong, please try again.';

      if (error instanceof Response) {
        const errorBody = await error.json();

        if (
          'detail' in errorBody &&
          Array.isArray(errorBody.detail) &&
          errorBody.detail.length > 0
        ) {
          submit = errorBody.detail[0].msg;
        } else if (errorBody.detail) {
          submit = errorBody.detail;
        }
      }

      return {
        errors: {
          submit,
        },
        resolved: false,
        newTasks: [],
      };
    }

    newTasks = await response.json();
  }

  setNewTasksForSession(
    taskId!,
    newTasks,
    locationId,
    resolverType as ResolverType,
  );

  return {
    errors: {},
    resolved: true,
    newTasks,
  };
};

export const loader: LoaderFunction = async ({ request, params }) => {
  const { wizardSlug, taskId } = params;

  saveCallbackUrlFromLoader({ request, params });

  return apiBatch({
    task: {
      url: `/api/compliance/tasks/${taskId}${wizardSlug ? `?wizard_public_id=${wizardSlug}` : ''}`,
      method: 'GET',
    },
    policyInstances: {
      url: `/api/compliance/tasks/${taskId}/policy_instances`,
      method: 'GET',
      onError: () => [],
    },
    users: {
      url: '/api/users',
      method: 'GET',
    },
  });
};

export const Component = () => {
  const { resolverType } = useParams<TaskRouteParams>();
  const tasks = useTasks();
  const { id, source } = useTask();
  const actionData = useTaskActionData();
  const resolverUrl = useResolverUrl();
  const standaloneTaskUrl = useStandaloneTaskUrl();
  const navigation = useNavigation();
  const nextResolverUrl = useNextResolverUrl();
  const isStandaloneRoute = useIsStandaloneRoute();
  const isAutomationForm = useIsAutomationRoute();
  const taskLocation = useTasksFrameworkLocation();
  const [isSubmissionSlow, setIsSubmissionSlow] = useState(false);

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;

    if (navigation.state === 'submitting') {
      timeout = setTimeout(() => {
        setIsSubmissionSlow(true);
      }, 5000);
    } else {
      setIsSubmissionSlow(false);

      if (timeout !== null) {
        clearTimeout(timeout);
      }
    }

    return () => {
      if (timeout !== null) {
        clearTimeout(timeout);
      }
    };
  }, [navigation.state]);

  /**
   * We only want to check if the current task ID is within the list of tasks
   * if we're are NOT on a standalone task route. Otherwise, we will cause an
   * infinite loop of redirects.
   */
  if (!standaloneTaskUrl && !tasks.find((_task) => _task.id === id)) {
    return <Navigate to={resolverUrl} replace />;
  }

  let content = null;

  if (actionData?.resolved && !isStandaloneRoute) {
    if (navigation.state === 'idle') {
      const newTasks = actionData.newTasks;
      const newTasksInTaskList = tasks.filter((_task) => {
        return newTasks.find((newTask) => newTask.id === _task.id);
      });

      if (
        nextResolverUrl &&
        (newTasksInTaskList.length === 0 ||
          resolverType === ResolverType.Assessment)
      ) {
        let state;

        if (resolverType === ResolverType.Assessment) {
          state = { newTasks };
        }

        return <Navigate to={nextResolverUrl} state={state} />;
      }

      content = <TaskSummary />;
    } else {
      content = (
        <div className="w-full">
          <Loading />
        </div>
      );
    }
  }

  if (!content) {
    if (isSubmissionSlow) {
      content = (
        <TaskContainer>
          <div className="flex w-full max-w-3xl flex-col items-center gap-y-2">
            <div className="mt-10 max-w-2xl">
              <LoadingTasks />
            </div>

            <h1 className="text-center text-4xl font-bold tracking-tight">
              Analyzing Requirements
            </h1>
            <p className="text-center text-zinc-800">
              Checking compliance rules
              {taskLocation ? ` for ${taskLocation.name}` : ''}…
            </p>
          </div>
        </TaskContainer>
      );
    } else if (
      source.type === TaskType.question ||
      source.type === TaskType.bulk_question
    ) {
      content = <TaskQuestion key={id} />;
    } else if (!isAutomationForm) {
      content = <TaskRequirement key={id} />;
    }
  }

  return (
    <section className="flex h-full max-w-screen-2xl overflow-y-auto @container/task">
      <ResolverSidebar />

      {content}
      <Outlet />
    </section>
  );
};
